import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, ImageOff, LoaderCircle, ShoppingBag, X } from "lucide-react";
import { formatINR, resolveProductImage } from "../../lib/utils-cynos";
import VariantSelectors from "../common/VariantSelectors";

const pendingKeyFor = (item) =>
  `${String(item.productId || "").trim()}::${String(item.selectedColor || "").trim().toLowerCase()}::${String(item.selectedSize || "").trim().toLowerCase()}`;

const imageFor = (item) => {
  const product = item.product || {};
  const colorKey = Object.keys(product.color_images || {}).find(
    (key) => key.toLowerCase() === String(item.selectedColor || "").toLowerCase()
  );
  return (colorKey && product.color_images[colorKey]?.[0]) || product.images?.[0] || "";
};

const WishlistModal = ({ open, onClose, items = [], loading = false, pendingKeys = {}, onAddToCart }) => {
  const [addingKeys, setAddingKeys] = useState({});

  const addItem = async (item) => {
    const key = pendingKeyFor(item);
    if (addingKeys[key]) return;
    setAddingKeys((current) => ({ ...current, [key]: true }));
    try {
      await onAddToCart(item);
    } finally {
      setAddingKeys((current) => ({ ...current, [key]: false }));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-wishlist-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.section
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="max-h-[88svh] w-full overflow-hidden border border-white/10 bg-cynos-card shadow-2xl sm:max-w-2xl"
            data-testid="checkout-wishlist-modal"
          >
            <header className="flex items-center justify-between border-b border-white/10 p-4 md:p-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cynos-red">Saved Drops</p>
                <h2 id="checkout-wishlist-title" className="mt-1 font-anton text-3xl uppercase">Add from wishlist</h2>
              </div>
              <button type="button" onClick={onClose} className="inline-flex h-11 w-11 items-center justify-center border border-white/10 text-zinc-300 transition hover:border-white/40 hover:text-white" aria-label="Close wishlist">
                <X size={20} />
              </button>
            </header>

            <div className="max-h-[calc(88svh-104px)] overflow-y-auto p-4 md:p-6">
              {loading ? (
                <div className="flex min-h-48 items-center justify-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">
                  <LoaderCircle size={18} className="animate-spin text-cynos-red" /> Loading wishlist
                </div>
              ) : items.length === 0 ? (
                <div className="flex min-h-56 flex-col items-center justify-center px-4 text-center">
                  <Heart size={32} className="text-cynos-red" />
                  <h3 className="mt-4 font-anton text-2xl uppercase">Your wishlist is empty.</h3>
                  <p className="mt-2 max-w-sm text-sm text-zinc-400">Save a drop first, then add it to your order without leaving checkout.</p>
                  <button type="button" onClick={onClose} className="mt-6 min-h-11 border border-white/20 px-5 font-mono text-xs uppercase tracking-[0.18em] transition hover:border-cynos-red hover:text-cynos-red">
                    Continue shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => {
                    const product = item.product;
                    const unavailable = !product || Number(product.stock_count ?? product.stock ?? 1) <= 0 || [0, 2].includes(Number(product?.active_status));
                    const key = pendingKeyFor(item);
                    const isAdding = Boolean(addingKeys[key]);
                    const isWishlistPending = Boolean(pendingKeys[key]);
                    const image = resolveProductImage(imageFor(item));

                    return (
                      <motion.article layout key={key} className="flex gap-3 border border-white/10 bg-black/20 p-3 md:gap-4 md:p-4">
                        <div className="relative h-[70px] w-[70px] shrink-0 overflow-hidden rounded-[7px] border border-white/10 bg-black md:h-20 md:w-20">
                          <ImageOff className="absolute inset-0 m-auto h-5 w-5 text-zinc-700" />
                          {image && <img src={image} alt={product?.name || "Wishlist product"} className="relative z-10 h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = "none"; }} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <h3 className="truncate text-sm font-bold uppercase text-white">{product?.name || "Unavailable product"}</h3>
                              <VariantSelectors
                                sizes={product?.sizes || []}
                                colors={product?.colors || []}
                                selectedSize={item.selectedSize || ""}
                                selectedColor={item.selectedColor || ""}
                                onSizeChange={() => {}}
                                onColorChange={() => {}}
                                disabled
                              />
                              <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.14em] ${unavailable ? "text-cynos-red" : "text-emerald-300"}`}>
                                {unavailable ? "Out of stock" : "In stock"}
                              </p>
                            </div>
                            {product && <span className="shrink-0 font-anton text-lg">{formatINR(product.price)}</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => addItem(item)}
                            disabled={unavailable || isAdding || isWishlistPending}
                            className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 bg-white px-4 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-black transition hover:bg-cynos-red hover:text-white active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
                          >
                            {isAdding ? <LoaderCircle size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                            {isAdding ? "Adding…" : "Add to cart"}
                          </button>
                        </div>
                      </motion.article>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WishlistModal;
